import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { EventDefinition, HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  BulkAdjustInventoryItemsByFilterApplicationErrors,
  BulkAdjustInventoryItemsByFilterOptions,
  BulkAdjustInventoryItemsByFilterResponse,
  BulkCreateInventoryItemsApplicationErrors,
  BulkCreateInventoryItemsByVariantFilterOptions,
  BulkCreateInventoryItemsByVariantFilterResponse,
  BulkCreateInventoryItemsOptions,
  BulkCreateInventoryItemsResponse,
  BulkCreateInventoryItemsValidationErrors,
  BulkDecrementInventoryItemsApplicationErrors,
  BulkDecrementInventoryItemsByVariantAndLocationApplicationErrors,
  BulkDecrementInventoryItemsByVariantAndLocationOptions,
  BulkDecrementInventoryItemsByVariantAndLocationResponse,
  BulkDecrementInventoryItemsOptions,
  BulkDecrementInventoryItemsResponse,
  BulkDeleteInventoryItemsByFilterOptions,
  BulkDeleteInventoryItemsByFilterResponse,
  BulkDeleteInventoryItemsResponse,
  BulkIncrementInventoryItemsApplicationErrors,
  BulkIncrementInventoryItemsByVariantAndLocationApplicationErrors,
  BulkIncrementInventoryItemsByVariantAndLocationOptions,
  BulkIncrementInventoryItemsByVariantAndLocationResponse,
  BulkIncrementInventoryItemsOptions,
  BulkIncrementInventoryItemsResponse,
  BulkSetOrAdjustInventoryItemsByFilterApplicationErrors,
  BulkSetOrAdjustInventoryItemsByFilterOptions,
  BulkSetOrAdjustInventoryItemsByFilterResponse,
  BulkUpdateInventoryItemsApplicationErrors,
  BulkUpdateInventoryItemsByFilterApplicationErrors,
  BulkUpdateInventoryItemsByFilterOptions,
  BulkUpdateInventoryItemsByFilterResponse,
  BulkUpdateInventoryItemsOptions,
  BulkUpdateInventoryItemsResponse,
  BulkUpsertInventoryItemsApplicationErrors,
  BulkUpsertInventoryItemsOptions,
  BulkUpsertInventoryItemsResponse,
  CreateInventoryItemApplicationErrors,
  CreateInventoryItemValidationErrors,
  DecrementDataById,
  DecrementDataByVariantAndLocation,
  IncrementDataById,
  IncrementDataByVariantAndLocation,
  InventoryItem,
  InventoryItemCreatedEnvelope,
  InventoryItemDeletedEnvelope,
  InventoryItemQuery,
  InventoryItemSearch,
  InventoryItemStockStatusUpdatedEnvelope,
  InventoryItemUpdatedEnvelope,
  InventoryItemUpdatedWithReasonEnvelope,
  InventoryItemsQueryBuilder,
  MaskedInventoryItem,
  QueryInventoryItemsResponse,
  SearchInventoryItemsResponse,
  UpdateInventoryItem,
  UpdateInventoryItemApplicationErrors,
  UpdateInventoryItemOptions,
  bulkAdjustInventoryItemsByFilter as universalBulkAdjustInventoryItemsByFilter,
  bulkCreateInventoryItems as universalBulkCreateInventoryItems,
  bulkCreateInventoryItemsByVariantFilter as universalBulkCreateInventoryItemsByVariantFilter,
  bulkDecrementInventoryItems as universalBulkDecrementInventoryItems,
  bulkDecrementInventoryItemsByVariantAndLocation as universalBulkDecrementInventoryItemsByVariantAndLocation,
  bulkDeleteInventoryItems as universalBulkDeleteInventoryItems,
  bulkDeleteInventoryItemsByFilter as universalBulkDeleteInventoryItemsByFilter,
  bulkIncrementInventoryItems as universalBulkIncrementInventoryItems,
  bulkIncrementInventoryItemsByVariantAndLocation as universalBulkIncrementInventoryItemsByVariantAndLocation,
  bulkSetOrAdjustInventoryItemsByFilter as universalBulkSetOrAdjustInventoryItemsByFilter,
  bulkUpdateInventoryItems as universalBulkUpdateInventoryItems,
  bulkUpdateInventoryItemsByFilter as universalBulkUpdateInventoryItemsByFilter,
  bulkUpsertInventoryItems as universalBulkUpsertInventoryItems,
  createInventoryItem as universalCreateInventoryItem,
  deleteInventoryItem as universalDeleteInventoryItem,
  getInventoryItem as universalGetInventoryItem,
  queryInventoryItems as universalQueryInventoryItems,
  searchInventoryItems as universalSearchInventoryItems,
  typedQueryInventoryItems as universalTypedQueryInventoryItems,
  updateInventoryItem as universalUpdateInventoryItem,
} from './stores-catalog-v3-inventory-item-inventory-items-v-3.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/stores' };

export function createInventoryItem(
  httpClient: HttpClient
): CreateInventoryItemSignature {
  return (
    inventoryItem: NonNullablePaths<InventoryItem, `productId` | `variantId`, 2>
  ) =>
    universalCreateInventoryItem(
      inventoryItem,
      // @ts-ignore
      { httpClient }
    );
}

interface CreateInventoryItemSignature {
  /**
   * Creates an inventory item.
   *
   * The combination of `variantId` and `locationId` must be unique.
   * @param - Inventory item to create.
   * @returns Created inventory item.
   */
  (
    inventoryItem: NonNullablePaths<InventoryItem, `productId` | `variantId`, 2>
  ): Promise<
    NonNullablePaths<
      InventoryItem,
      | `inStock`
      | `quantity`
      | `variantId`
      | `productId`
      | `trackQuantity`
      | `availabilityStatus`
      | `product.directCategoryIds`,
      3
    > & {
      __applicationErrorsType?: CreateInventoryItemApplicationErrors;
      __validationErrorsType?: CreateInventoryItemValidationErrors;
    }
  >;
}

export function bulkCreateInventoryItems(
  httpClient: HttpClient
): BulkCreateInventoryItemsSignature {
  return (
    inventoryItems: NonNullablePaths<
      InventoryItem,
      `productId` | `variantId`,
      2
    >[],
    options?: BulkCreateInventoryItemsOptions
  ) =>
    universalBulkCreateInventoryItems(
      inventoryItems,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkCreateInventoryItemsSignature {
  /**
   * Creates multiple inventory items.
   *
   * The combination of `variantId` and `locationId` must be unique for each inventory item.
   * @param - Inventory items to create.
   */
  (
    inventoryItems: NonNullablePaths<
      InventoryItem,
      `productId` | `variantId`,
      2
    >[],
    options?: BulkCreateInventoryItemsOptions
  ): Promise<
    NonNullablePaths<
      BulkCreateInventoryItemsResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `results.${number}.item.inStock`
      | `results.${number}.item.quantity`
      | `results.${number}.item.variantId`
      | `results.${number}.item.productId`
      | `results.${number}.item.trackQuantity`
      | `results.${number}.item.availabilityStatus`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    > & {
      __applicationErrorsType?: BulkCreateInventoryItemsApplicationErrors;
      __validationErrorsType?: BulkCreateInventoryItemsValidationErrors;
    }
  >;
}

export function getInventoryItem(
  httpClient: HttpClient
): GetInventoryItemSignature {
  return (inventoryItemId: string) =>
    universalGetInventoryItem(
      inventoryItemId,
      // @ts-ignore
      { httpClient }
    );
}

interface GetInventoryItemSignature {
  /**
   * Retrieves an inventory item.
   * @param - Inventory item ID.
   * @returns Inventory item.
   */
  (inventoryItemId: string): Promise<
    NonNullablePaths<
      InventoryItem,
      | `inStock`
      | `quantity`
      | `variantId`
      | `productId`
      | `trackQuantity`
      | `availabilityStatus`
      | `product.directCategoryIds`,
      3
    >
  >;
}

export function updateInventoryItem(
  httpClient: HttpClient
): UpdateInventoryItemSignature {
  return (
    _id: string,
    inventoryItem: NonNullablePaths<UpdateInventoryItem, `revision`, 2>,
    options?: UpdateInventoryItemOptions
  ) =>
    universalUpdateInventoryItem(
      _id,
      inventoryItem,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateInventoryItemSignature {
  /**
   * Updates an inventory item.
   *
   *
   * Each time the inventory item is updated, `revision` increments by 1.
   * The current `revision` must be passed when updating the inventory item.
   * This ensures you're working with the latest inventory item and prevents unintended overwrites.
   * @param - Inventory item ID.
   * @returns Updated inventory item.
   */
  (
    _id: string,
    inventoryItem: NonNullablePaths<UpdateInventoryItem, `revision`, 2>,
    options?: UpdateInventoryItemOptions
  ): Promise<
    NonNullablePaths<
      InventoryItem,
      | `inStock`
      | `quantity`
      | `variantId`
      | `productId`
      | `trackQuantity`
      | `availabilityStatus`
      | `product.directCategoryIds`,
      3
    > & {
      __applicationErrorsType?: UpdateInventoryItemApplicationErrors;
    }
  >;
}

export function bulkUpdateInventoryItems(
  httpClient: HttpClient
): BulkUpdateInventoryItemsSignature {
  return (
    inventoryItems: NonNullablePaths<
      MaskedInventoryItem,
      `inventoryItem._id` | `inventoryItem.revision`,
      3
    >[],
    options?: BulkUpdateInventoryItemsOptions
  ) =>
    universalBulkUpdateInventoryItems(
      inventoryItems,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkUpdateInventoryItemsSignature {
  /**
   * Updates multiple inventory items.
   *
   * Each time an inventory item is updated, `revision` increments by 1.
   * The current `revision` must be passed when updating an inventory item.
   * This ensures you're working with the latest inventory item and prevents unintended overwrites.
   * @param - Inventory items to update.
   */
  (
    inventoryItems: NonNullablePaths<
      MaskedInventoryItem,
      `inventoryItem._id` | `inventoryItem.revision`,
      3
    >[],
    options?: BulkUpdateInventoryItemsOptions
  ): Promise<
    NonNullablePaths<
      BulkUpdateInventoryItemsResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `results.${number}.item.inStock`
      | `results.${number}.item.quantity`
      | `results.${number}.item.variantId`
      | `results.${number}.item.productId`
      | `results.${number}.item.trackQuantity`
      | `results.${number}.item.availabilityStatus`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    > & {
      __applicationErrorsType?: BulkUpdateInventoryItemsApplicationErrors;
    }
  >;
}

/** @internal */
export function bulkUpsertInventoryItems(
  httpClient: HttpClient
): BulkUpsertInventoryItemsSignature {
  return (
    inventoryItems: NonNullablePaths<
      MaskedInventoryItem,
      `inventoryItem` | `inventoryItem.productId` | `inventoryItem.variantId`,
      3
    >[],
    options?: BulkUpsertInventoryItemsOptions
  ) =>
    universalBulkUpsertInventoryItems(
      inventoryItems,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkUpsertInventoryItemsSignature {
  /**
   * Creates or updates multiple inventory items.
   *
   * The upsert key is the combination of `productId`, `variantId`, and `locationId`.
   * If `locationId` isn't provided, the site's default location is used.
   *
   * For newly created items, `field_mask` is ignored and all provided fields are applied.
   * For existing items, only the fields specified in `field_mask` are updated.
   * @param - Inventory items to create or update.
   */
  (
    inventoryItems: NonNullablePaths<
      MaskedInventoryItem,
      `inventoryItem` | `inventoryItem.productId` | `inventoryItem.variantId`,
      3
    >[],
    options?: BulkUpsertInventoryItemsOptions
  ): Promise<
    NonNullablePaths<
      BulkUpsertInventoryItemsResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `results.${number}.item.inStock`
      | `results.${number}.item.quantity`
      | `results.${number}.item.variantId`
      | `results.${number}.item.productId`
      | `results.${number}.item.trackQuantity`
      | `results.${number}.item.availabilityStatus`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    > & {
      __applicationErrorsType?: BulkUpsertInventoryItemsApplicationErrors;
    }
  >;
}

export function bulkUpdateInventoryItemsByFilter(
  httpClient: HttpClient
): BulkUpdateInventoryItemsByFilterSignature {
  return (
    filter: Record<string, any>,
    options?: NonNullablePaths<
      BulkUpdateInventoryItemsByFilterOptions,
      `inventoryItem`,
      2
    >
  ) =>
    universalBulkUpdateInventoryItemsByFilter(
      filter,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkUpdateInventoryItemsByFilterSignature {
  /**
   * Updates multiple inventory items, given the provided filter.
   *
   * Each time an inventory item is updated, `revision` increments by 1.
   * The current `revision` must be passed when updating an inventory item.
   * This ensures you're working with the latest inventory item and prevents unintended overwrites.
   * @param - Filter object.
   */
  (
    filter: Record<string, any>,
    options?: NonNullablePaths<
      BulkUpdateInventoryItemsByFilterOptions,
      `inventoryItem`,
      2
    >
  ): Promise<
    NonNullablePaths<BulkUpdateInventoryItemsByFilterResponse, `jobId`, 2> & {
      __applicationErrorsType?: BulkUpdateInventoryItemsByFilterApplicationErrors;
    }
  >;
}

export function deleteInventoryItem(
  httpClient: HttpClient
): DeleteInventoryItemSignature {
  return (inventoryItemId: string) =>
    universalDeleteInventoryItem(
      inventoryItemId,
      // @ts-ignore
      { httpClient }
    );
}

interface DeleteInventoryItemSignature {
  /**
   * Deletes an inventory item.
   * @param - Inventory item ID.
   */
  (inventoryItemId: string): Promise<void>;
}

export function bulkDeleteInventoryItems(
  httpClient: HttpClient
): BulkDeleteInventoryItemsSignature {
  return (inventoryItemIds: string[]) =>
    universalBulkDeleteInventoryItems(
      inventoryItemIds,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkDeleteInventoryItemsSignature {
  /**
   * Deletes multiple inventory items.
   * @param - IDs of inventory items to delete.
   */
  (inventoryItemIds: string[]): Promise<
    NonNullablePaths<
      BulkDeleteInventoryItemsResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    >
  >;
}

/** @internal */
export function bulkDeleteInventoryItemsByFilter(
  httpClient: HttpClient
): BulkDeleteInventoryItemsByFilterSignature {
  return (
    filter: Record<string, any>,
    options?: BulkDeleteInventoryItemsByFilterOptions
  ) =>
    universalBulkDeleteInventoryItemsByFilter(
      filter,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkDeleteInventoryItemsByFilterSignature {
  /**
   * Deletes multiple inventory items, given the provided filter.
   *
   * The operation runs asynchronously. Use the returned job ID to track progress.
   * @param - Filter object in the following format:
   * `"filter" : {
   * "fieldName1": "value1",
   * "fieldName2":{"$operator":"value2"}
   * }`
   * Example of operators: `$eq`, `$ne`, `$lt`, `$lte`, `$gt`, `$gte`, `$in`, `$hasSome`, `$hasAll`, `$startsWith`, `$contains`
   */
  (
    filter: Record<string, any>,
    options?: BulkDeleteInventoryItemsByFilterOptions
  ): Promise<
    NonNullablePaths<BulkDeleteInventoryItemsByFilterResponse, `jobId`, 2>
  >;
}

export function queryInventoryItems(
  httpClient: HttpClient
): QueryInventoryItemsSignature {
  return () =>
    universalQueryInventoryItems(
      // @ts-ignore
      { httpClient }
    );
}

interface QueryInventoryItemsSignature {
  /**
   * Retrieves a list of up to 1,000 inventory items, given the provided filtering, sorting, and cursor paging.
   *
   * To learn about working with _Query_ endpoints, see
   * [API Query Language](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-the-wix-api-query-language),
   * and [Sorting and Paging](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-sorting-and-paging).
   * Learn more about the differences between [_Query_ and _Search_](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-search-query-and-list-methods) methods.
   */
  (): InventoryItemsQueryBuilder;
}

export function typedQueryInventoryItems(
  httpClient: HttpClient
): TypedQueryInventoryItemsSignature {
  return (query: InventoryItemQuery) =>
    universalTypedQueryInventoryItems(
      query,
      // @ts-ignore
      { httpClient }
    );
}

interface TypedQueryInventoryItemsSignature {
  /** */
  (query: InventoryItemQuery): Promise<
    NonNullablePaths<
      QueryInventoryItemsResponse,
      | `inventoryItems`
      | `inventoryItems.${number}.inStock`
      | `inventoryItems.${number}.quantity`
      | `inventoryItems.${number}.variantId`
      | `inventoryItems.${number}.productId`
      | `inventoryItems.${number}.trackQuantity`
      | `inventoryItems.${number}.availabilityStatus`,
      4
    >
  >;
}

export function searchInventoryItems(
  httpClient: HttpClient
): SearchInventoryItemsSignature {
  return (search: InventoryItemSearch) =>
    universalSearchInventoryItems(
      search,
      // @ts-ignore
      { httpClient }
    );
}

interface SearchInventoryItemsSignature {
  /**
   * Retrieves a list of inventory items, given the provided filtering, sorting, and cursor paging.
   *
   *
   * Search Inventory Items runs with these defaults, which you can override:
   *
   * - `createdDate` is sorted in `DESC` order
   * - `cursorPaging.limit` is `100`
   *
   * To learn about working with _Search_ endpoints, see
   * [API Query Language](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-the-wix-api-query-language),
   * and [Sorting and Paging](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-sorting-and-paging).
   *
   * Learn more about the differences between [_Query_ and _Search_](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-search-query-and-list-methods) methods.
   * @param - Search options.
   */
  (search: InventoryItemSearch): Promise<
    NonNullablePaths<
      SearchInventoryItemsResponse,
      | `inventoryItems`
      | `inventoryItems.${number}.inStock`
      | `inventoryItems.${number}.quantity`
      | `inventoryItems.${number}.variantId`
      | `inventoryItems.${number}.productId`
      | `inventoryItems.${number}.trackQuantity`
      | `inventoryItems.${number}.availabilityStatus`
      | `aggregationData.results`
      | `aggregationData.results.${number}.scalar.type`
      | `aggregationData.results.${number}.scalar.value`
      | `aggregationData.results.${number}.name`
      | `aggregationData.results.${number}.type`
      | `aggregationData.results.${number}.fieldPath`,
      6
    >
  >;
}

export function bulkDecrementInventoryItems(
  httpClient: HttpClient
): BulkDecrementInventoryItemsSignature {
  return (
    decrementData: NonNullablePaths<
      DecrementDataById,
      `decrementBy` | `inventoryItemId`,
      2
    >[],
    options?: BulkDecrementInventoryItemsOptions
  ) =>
    universalBulkDecrementInventoryItems(
      decrementData,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkDecrementInventoryItemsSignature {
  /**
   * Decrements quantities of multiple inventory items.
   *
   * > **Notes:**:
   * > + `trackQuantity` must be `true` to allow for decrementing the quantity.
   * > + If you pass `restrictInventory: true` and the `decrementData.decrementBy` amount is greater than the current quantity in stock, the request will fail with an `INSUFFICIENT_INVENTORY` error.
   * > + Pass `restrictInventory: false` to allow for negative quantities.
   * > + If you pass `preorderRequest: true` and the item is available for preorder, the item's `preorderCounter` will increase and the item's quantity will stay the same.
   * @param - Inventory item IDs and decrement data.
   */
  (
    decrementData: NonNullablePaths<
      DecrementDataById,
      `decrementBy` | `inventoryItemId`,
      2
    >[],
    options?: BulkDecrementInventoryItemsOptions
  ): Promise<
    NonNullablePaths<
      BulkDecrementInventoryItemsResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `results.${number}.item.inStock`
      | `results.${number}.item.quantity`
      | `results.${number}.item.variantId`
      | `results.${number}.item.productId`
      | `results.${number}.item.trackQuantity`
      | `results.${number}.item.availabilityStatus`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    > & {
      __applicationErrorsType?: BulkDecrementInventoryItemsApplicationErrors;
    }
  >;
}

export function bulkIncrementInventoryItems(
  httpClient: HttpClient
): BulkIncrementInventoryItemsSignature {
  return (
    incrementData: NonNullablePaths<
      IncrementDataById,
      `incrementBy` | `inventoryItemId`,
      2
    >[],
    options?: BulkIncrementInventoryItemsOptions
  ) =>
    universalBulkIncrementInventoryItems(
      incrementData,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkIncrementInventoryItemsSignature {
  /**
   * Increments quantities of multiple inventory items.
   *
   * > **Note:** `trackQuantity` must be `true` to allow for incrementing the quantity.
   * @param - Inventory item IDs and increment data.
   */
  (
    incrementData: NonNullablePaths<
      IncrementDataById,
      `incrementBy` | `inventoryItemId`,
      2
    >[],
    options?: BulkIncrementInventoryItemsOptions
  ): Promise<
    NonNullablePaths<
      BulkIncrementInventoryItemsResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `results.${number}.item.inStock`
      | `results.${number}.item.quantity`
      | `results.${number}.item.variantId`
      | `results.${number}.item.productId`
      | `results.${number}.item.trackQuantity`
      | `results.${number}.item.availabilityStatus`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    > & {
      __applicationErrorsType?: BulkIncrementInventoryItemsApplicationErrors;
    }
  >;
}

export function bulkDecrementInventoryItemsByVariantAndLocation(
  httpClient: HttpClient
): BulkDecrementInventoryItemsByVariantAndLocationSignature {
  return (
    decrementData: NonNullablePaths<
      DecrementDataByVariantAndLocation,
      `decrementBy` | `variantId`,
      2
    >[],
    options?: BulkDecrementInventoryItemsByVariantAndLocationOptions
  ) =>
    universalBulkDecrementInventoryItemsByVariantAndLocation(
      decrementData,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkDecrementInventoryItemsByVariantAndLocationSignature {
  /**
   * Decrements quantities of multiple inventory items by variant and location.
   *
   * > **Notes:**:
   * > + `trackQuantity` must be `true` to allow for decrementing the quantity.
   * > + If you pass `restrictInventory: true` and the `decrementData.decrementBy` amount is greater than the current quantity in stock, the request will fail with an `INSUFFICIENT_INVENTORY` error.
   * > + Pass `restrictInventory: false` to allow for negative quantities.
   * > + If you pass `preorderRequest: true` and the item is available for preorder, the item's `preorderCounter` will increase and the item's quantity will stay the same.
   * @param - Variant and location IDs, as well as decrement data.
   */
  (
    decrementData: NonNullablePaths<
      DecrementDataByVariantAndLocation,
      `decrementBy` | `variantId`,
      2
    >[],
    options?: BulkDecrementInventoryItemsByVariantAndLocationOptions
  ): Promise<
    NonNullablePaths<
      BulkDecrementInventoryItemsByVariantAndLocationResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `results.${number}.item.inStock`
      | `results.${number}.item.quantity`
      | `results.${number}.item.variantId`
      | `results.${number}.item.productId`
      | `results.${number}.item.trackQuantity`
      | `results.${number}.item.availabilityStatus`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    > & {
      __applicationErrorsType?: BulkDecrementInventoryItemsByVariantAndLocationApplicationErrors;
    }
  >;
}

export function bulkIncrementInventoryItemsByVariantAndLocation(
  httpClient: HttpClient
): BulkIncrementInventoryItemsByVariantAndLocationSignature {
  return (
    incrementData: NonNullablePaths<
      IncrementDataByVariantAndLocation,
      `incrementBy` | `variantId`,
      2
    >[],
    options?: BulkIncrementInventoryItemsByVariantAndLocationOptions
  ) =>
    universalBulkIncrementInventoryItemsByVariantAndLocation(
      incrementData,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkIncrementInventoryItemsByVariantAndLocationSignature {
  /**
   * Increments quantities of multiple inventory items by variant and location.
   *
   * > **Note:** `trackQuantity` must be `true` to allow for incrementing the quantity.
   * @param - Variant and location IDs, as well as increment data.
   */
  (
    incrementData: NonNullablePaths<
      IncrementDataByVariantAndLocation,
      `incrementBy` | `variantId`,
      2
    >[],
    options?: BulkIncrementInventoryItemsByVariantAndLocationOptions
  ): Promise<
    NonNullablePaths<
      BulkIncrementInventoryItemsByVariantAndLocationResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `results.${number}.item.inStock`
      | `results.${number}.item.quantity`
      | `results.${number}.item.variantId`
      | `results.${number}.item.productId`
      | `results.${number}.item.trackQuantity`
      | `results.${number}.item.availabilityStatus`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    > & {
      __applicationErrorsType?: BulkIncrementInventoryItemsByVariantAndLocationApplicationErrors;
    }
  >;
}

/** @internal */
export function bulkSetOrAdjustInventoryItemsByFilter(
  httpClient: HttpClient
): BulkSetOrAdjustInventoryItemsByFilterSignature {
  return (
    filter: Record<string, any>,
    options?: NonNullablePaths<
      BulkSetOrAdjustInventoryItemsByFilterOptions,
      `adjustedInventoryAction`,
      2
    >
  ) =>
    universalBulkSetOrAdjustInventoryItemsByFilter(
      filter,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkSetOrAdjustInventoryItemsByFilterSignature {
  /**
   * Set or Adjust inventory items which satisfy the provided filter.
   * If item track quantity, adjust it's quantity,
   * In case non-tracking, updates it to track and sets quantity to `incrementBy` or `decrementBy`.
   * @param - Filter object in the following format:
   * `"filter" : {
   * "fieldName1": "value1",
   * "fieldName2":{"$operator":"value2"}
   * }`
   * Example of operators: `$eq`, `$ne`, `$lt`, `$lte`, `$gt`, `$gte`, `$in`, `$hasSome`, `$hasAll`, `$startsWith`, `$contains`
   * @deprecated
   */
  (
    filter: Record<string, any>,
    options?: NonNullablePaths<
      BulkSetOrAdjustInventoryItemsByFilterOptions,
      `adjustedInventoryAction`,
      2
    >
  ): Promise<
    NonNullablePaths<
      BulkSetOrAdjustInventoryItemsByFilterResponse,
      `jobId`,
      2
    > & {
      __applicationErrorsType?: BulkSetOrAdjustInventoryItemsByFilterApplicationErrors;
    }
  >;
}

/** @internal */
export function bulkAdjustInventoryItemsByFilter(
  httpClient: HttpClient
): BulkAdjustInventoryItemsByFilterSignature {
  return (
    filter: Record<string, any>,
    options?: NonNullablePaths<
      BulkAdjustInventoryItemsByFilterOptions,
      `adjustment`,
      2
    >
  ) =>
    universalBulkAdjustInventoryItemsByFilter(
      filter,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkAdjustInventoryItemsByFilterSignature {
  /**
   * Adjust quantity of inventory items which satisfy the provided filter.
   * For items that track quantity: Adjusts the quantity based on incrementBy or decrementBy.
   * For items that do not track quantity:
   * if track_quantity is false (default), found items that are non-tracking will fail.
   * if track_quantity is true, non-tracking items will be updated to track quantity and quantity will be set to the provided amount.
   * @param - Filter object in the following format:
   * `"filter" : {
   * "fieldName1": "value1",
   * "fieldName2":{"$operator":"value2"}
   * }`
   * Example of operators: `$eq`, `$ne`, `$lt`, `$lte`, `$gt`, `$gte`, `$in`, `$hasSome`, `$hasAll`, `$startsWith`, `$contains`
   */
  (
    filter: Record<string, any>,
    options?: NonNullablePaths<
      BulkAdjustInventoryItemsByFilterOptions,
      `adjustment`,
      2
    >
  ): Promise<
    NonNullablePaths<BulkAdjustInventoryItemsByFilterResponse, `jobId`, 2> & {
      __applicationErrorsType?: BulkAdjustInventoryItemsByFilterApplicationErrors;
    }
  >;
}

/** @internal */
export function bulkCreateInventoryItemsByVariantFilter(
  httpClient: HttpClient
): BulkCreateInventoryItemsByVariantFilterSignature {
  return (
    filter: Record<string, any>,
    options?: NonNullablePaths<
      BulkCreateInventoryItemsByVariantFilterOptions,
      `inventoryCreationData.quantity` | `locationId`,
      3
    >
  ) =>
    universalBulkCreateInventoryItemsByVariantFilter(
      filter,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkCreateInventoryItemsByVariantFilterSignature {
  /**
   * Creates inventory items at a target location for variants matching a filter.
   *
   * Uses variants reader to filter items by criteria (e.g. categories) and creates
   * inventory items at the specified location for each matching variant.
   *
   * The operation runs asynchronously. Use the returned job ID to track progress.
   * @param - Filter for variants. See [API Query Language](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-the-wix-api-query-language)
   * for WQL syntax and [Search Variants](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/product-variants-reader/search-variants)
   * for available filter fields.
   */
  (
    filter: Record<string, any>,
    options?: NonNullablePaths<
      BulkCreateInventoryItemsByVariantFilterOptions,
      `inventoryCreationData.quantity` | `locationId`,
      3
    >
  ): Promise<
    NonNullablePaths<
      BulkCreateInventoryItemsByVariantFilterResponse,
      `jobId`,
      2
    >
  >;
}

export const onInventoryItemCreated = EventDefinition(
  'wix.stores.catalog.v3.inventory_item_created',
  true,
  (event: InventoryItemCreatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<InventoryItemCreatedEnvelope>();
export const onInventoryItemDeleted = EventDefinition(
  'wix.stores.catalog.v3.inventory_item_deleted',
  true,
  (event: InventoryItemDeletedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<InventoryItemDeletedEnvelope>();
export const onInventoryItemStockStatusUpdated = EventDefinition(
  'wix.stores.catalog.v3.inventory_item_stock_status_updated',
  true,
  (event: InventoryItemStockStatusUpdatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [{ path: 'metadata.eventTime' }],
        },
      ])
    )
)<InventoryItemStockStatusUpdatedEnvelope>();
export const onInventoryItemUpdated = EventDefinition(
  'wix.stores.catalog.v3.inventory_item_updated',
  true,
  (event: InventoryItemUpdatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'metadata.eventTime' },
            { path: 'modifiedFields.createdDate' },
            { path: 'modifiedFields.updatedDate' },
          ],
        },
      ])
    )
)<InventoryItemUpdatedEnvelope>();
export const onInventoryItemUpdatedWithReason = EventDefinition(
  'wix.stores.catalog.v3.inventory_item_updated_with_reason',
  true,
  (event: InventoryItemUpdatedWithReasonEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'data.currentEntity.createdDate' },
            { path: 'data.currentEntity.updatedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<InventoryItemUpdatedWithReasonEnvelope>();

export {
  AccountInfo,
  AccountInfoMetadata,
  ActionEvent,
  AdjustInventoryAction,
  AdjustInventoryActionActionOneOf,
  Aggregation,
  AggregationData,
  AggregationKindOneOf,
  AggregationResults,
  AggregationResultsResultOneOf,
  AggregationResultsScalarResult,
  AggregationType,
  App,
  ApplicationError,
  AvailabilityStatus,
  BaseEventMetadata,
  Brand,
  BulkActionMetadata,
  BulkAdjustInventoryItemsByFilterOptions,
  BulkAdjustInventoryItemsByFilterRequest,
  BulkAdjustInventoryItemsByFilterResponse,
  BulkCreateInventoryItemsByVariantFilterOptions,
  BulkCreateInventoryItemsByVariantFilterRequest,
  BulkCreateInventoryItemsByVariantFilterResponse,
  BulkCreateInventoryItemsOptions,
  BulkCreateInventoryItemsRequest,
  BulkCreateInventoryItemsResponse,
  BulkDecrementInventoryItemsByVariantAndLocationOptions,
  BulkDecrementInventoryItemsByVariantAndLocationRequest,
  BulkDecrementInventoryItemsByVariantAndLocationResponse,
  BulkDecrementInventoryItemsOptions,
  BulkDecrementInventoryItemsRequest,
  BulkDecrementInventoryItemsResponse,
  BulkDeleteInventoryItemsByFilterOptions,
  BulkDeleteInventoryItemsByFilterRequest,
  BulkDeleteInventoryItemsByFilterResponse,
  BulkDeleteInventoryItemsRequest,
  BulkDeleteInventoryItemsResponse,
  BulkIncrementInventoryItemsByVariantAndLocationOptions,
  BulkIncrementInventoryItemsByVariantAndLocationRequest,
  BulkIncrementInventoryItemsByVariantAndLocationResponse,
  BulkIncrementInventoryItemsOptions,
  BulkIncrementInventoryItemsRequest,
  BulkIncrementInventoryItemsResponse,
  BulkInventoryItemAction,
  BulkInventoryItemResult,
  BulkSetOrAdjustInventoryItemsByFilterOptions,
  BulkSetOrAdjustInventoryItemsByFilterRequest,
  BulkSetOrAdjustInventoryItemsByFilterResponse,
  BulkUpdateInventoryItemsByFilterOptions,
  BulkUpdateInventoryItemsByFilterRequest,
  BulkUpdateInventoryItemsByFilterResponse,
  BulkUpdateInventoryItemsOptions,
  BulkUpdateInventoryItemsRequest,
  BulkUpdateInventoryItemsResponse,
  BulkUpsertInventoryItemResult,
  BulkUpsertInventoryItemsOptions,
  BulkUpsertInventoryItemsRequest,
  BulkUpsertInventoryItemsResponse,
  CatalogRollbackRequested,
  CommonCursors,
  CreateInventoryItemRequest,
  CreateInventoryItemResponse,
  CursorPaging,
  CursorPagingMetadata,
  CursorSearch,
  CursorSearchPagingMethodOneOf,
  Cursors,
  CustomTag,
  DateHistogramAggregation,
  DateHistogramResult,
  DateHistogramResults,
  DecrementDataById,
  DecrementDataByVariantAndLocation,
  DeleteInventoryItemRequest,
  DeleteInventoryItemResponse,
  DomainEvent,
  DomainEventBodyOneOf,
  Empty,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  EventMetadata,
  EventType,
  ExtendedFields,
  File,
  GetInventoryItemRequest,
  GetInventoryItemResponse,
  GroupByAggregation,
  GroupByAggregationKindOneOf,
  GroupByValueResults,
  IdentificationData,
  IdentificationDataIdOneOf,
  IncludeMissingValuesOptions,
  IncrementDataById,
  IncrementDataByVariantAndLocation,
  Interval,
  InvalidateCache,
  InvalidateCacheGetByOneOf,
  InventoryCreationData,
  InventoryItem,
  InventoryItemCreatedEnvelope,
  InventoryItemDeletedEnvelope,
  InventoryItemQuerySpec,
  InventoryItemSearchSpec,
  InventoryItemStockStatusUpdatedEnvelope,
  InventoryItemStockStatusUpdatedEvent,
  InventoryItemTrackingMethodOneOf,
  InventoryItemUpdatedEnvelope,
  InventoryItemUpdatedWithReason,
  InventoryItemUpdatedWithReasonEnvelope,
  InventoryItemsQueryBuilder,
  InventoryItemsQueryResult,
  ItemMetadata,
  MaskedInventoryItem,
  MessageEnvelope,
  MissingValues,
  Mode,
  NestedAggregation,
  NestedAggregationItem,
  NestedAggregationItemKindOneOf,
  NestedAggregationResults,
  NestedAggregationResultsResultOneOf,
  NestedAggregationType,
  NestedResultValue,
  NestedResultValueResultOneOf,
  NestedResults,
  NestedValueAggregationResult,
  OffsetSearch,
  OffsetSearchPagingMethodOneOf,
  Page,
  Pages,
  Paging,
  PagingMetadata,
  PlatformPagingMetadataV2,
  PreorderInfo,
  Product,
  QueryInventoryItemsRequest,
  QueryInventoryItemsResponse,
  QueryV2,
  QueryV2PagingMethodOneOf,
  RangeAggregation,
  RangeAggregationResult,
  RangeBucket,
  RangeResult,
  RangeResults,
  ReasonType,
  RecloneSiteRequest,
  RecloneSiteResponse,
  RestoreInfo,
  Results,
  ScalarAggregation,
  ScalarResult,
  ScalarType,
  SearchDetails,
  SearchInventoryItemsRequest,
  SearchInventoryItemsResponse,
  SearchInventoryItemsWithOffsetRequest,
  SearchInventoryItemsWithOffsetResponse,
  SortDirection,
  SortOrder,
  SortType,
  Sorting,
  URI,
  URIs,
  UpdateInventoryItem,
  UpdateInventoryItemOptions,
  UpdateInventoryItemRequest,
  UpdateInventoryItemResponse,
  V3BulkInventoryItemResult,
  ValueAggregation,
  ValueAggregationOptionsOneOf,
  ValueAggregationResult,
  ValueResult,
  ValueResults,
  WebhookIdentityType,
  utils,
} from './stores-catalog-v3-inventory-item-inventory-items-v-3.universal.js';
